import { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { GridColDef } from "@mui/x-data-grid-premium";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";

const OVERFLOW_EPS = 1;

const HEADER_TOOLTIP_SLOT_PROPS = {
  popper: {
    sx: {
      zIndex: (theme: { zIndex: { modal: number } }) =>
        theme.zIndex.modal +
        ELEVATED_DIALOG_Z_INDEX_OFFSET +
        ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
    },
  },
} as const;

const HEADER_LABEL_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#0D0D0D",
} as const;

export function AssessmentCenterTruncatedColumnHeader({
  label,
}: Readonly<{ label: string }>) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setTruncated(false);
      return;
    }
    const measure = () => {
      setTruncated(el.scrollWidth > el.clientWidth + OVERFLOW_EPS);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);

  return (
    <Tooltip
      title={label}
      arrow
      placement="top"
      disableHoverListener={!truncated}
      disableInteractive
      slotProps={HEADER_TOOLTIP_SLOT_PROPS}
    >
      <Box
        component="span"
        ref={ref}
        sx={HEADER_LABEL_SX}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

/** Adds ellipsis + hover tooltip to column headers that do not define `renderHeader`. */
export function withAssessmentCenterHeaderTooltips(
  columns: readonly GridColDef[],
): GridColDef[] {
  return columns.map((col) => {
    if (col.renderHeader) return col;
    const label = String(col.headerName ?? col.field ?? "");
    return {
      ...col,
      renderHeader: () => (
        <AssessmentCenterTruncatedColumnHeader label={label} />
      ),
    };
  });
}
